import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRef } from 'react';
import {
  useMenu,
  useMenuItem,
  useMenuSection,
  useSeparator,
  useSubmenuTrigger,
  useFocusRing,
  mergeProps,
} from 'react-aria';
import {
  useTreeState,
  useSubmenuTriggerState,
  Item as CollectionItem,
  Section as CollectionSection,
} from 'react-stately';

import { mergeRefs } from '../../lib/mergeRefs';
import { Overlay } from '../overlay/Overlay';

import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';

/** @import { JSX, ReactNode, RefObject } from 'react' */

/** @typedef {{ isPressed: boolean, isOpen: boolean }} MenuItemRenderState */

/**
 * @typedef {object} MenuItemExtraProps
 * @property {(state: MenuItemRenderState) => import('react').ReactNode} [renderContent] Renders content
 *   with the item's interaction state. `children` supplies the collection's fallback content.
 * @property {'true' | 'page'} [aria-current] Marks the item as the current one, e.g. the open page.
 * @property {(submenuProps: import('react-aria').SubmenuTriggerAria<any>['submenuProps'], submenuRef: RefObject<HTMLUListElement | null>) => ReactNode} [renderSubmenu]
 *   Makes the item open a submenu, usually another `Menu`. Needs `menuTriggerState` on the `Menu`.
 * @property {object} [submenuOverlayProps] Props for the submenu's `Overlay`, like `offset`.
 */

/** @type {<T>(props: import('react-stately').ItemProps<T> & MenuItemExtraProps) => JSX.Element} */
export const MenuItem = CollectionItem;

/** Group of menu items, with a divider above it when it follows another group. */
export const MenuSection = CollectionSection;

/**
 * @typedef {object} MenuExtraProps
 * @property {import('react-stately').CollectionChildren<any>} children
 * @property {(key: any) => void} [onAction]
 * @property {import('react-stately').RootMenuTriggerState} [menuTriggerState] Tracks the open
 *   submenu. Needed when an item has `renderSubmenu`.
 * @property {RefObject<HTMLUListElement | null>} [ref] The submenu's ref, from
 *   `renderSubmenu`.
 */

/**
 * @param {Omit<import('react-aria').AriaMenuOptions<any>, 'onAction'> & MenuExtraProps} props
 */
export function Menu(props) {
  const internalRef = useRef();
  const menuRef = props.ref ?? internalRef;
  const treeState = useTreeState({ ...props, selectionMode: 'none' });
  const menu = useMenu(props, treeState, menuRef);

  // Overlays only close on Escape when they are the topmost one, so while a hover-opened submenu
  // is on top, Escape from this menu closes the submenu first.
  function handleKeyDown(event) {
    const openSubmenuKey = props.menuTriggerState?.expandedKeysStack[0];

    if (event.key !== 'Escape' || openSubmenuKey == null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    props.menuTriggerState.closeSubmenu(openSubmenuKey, 0);
  }

  function renderItem(item) {
    if (item.props.renderSubmenu != null && props.menuTriggerState != null) {
      return (
        <SubmenuItem
          key={item.key}
          item={item}
          treeState={treeState}
          menuTriggerState={props.menuTriggerState}
          parentMenuRef={menuRef}
        />
      );
    }

    return <Item key={item.key} item={item} treeState={treeState} />;
  }

  return (
    <MenuContainer {...mergeProps(menu.menuProps, { onKeyDown: handleKeyDown })} ref={menuRef}>
      {[...treeState.collection].map((node, index) => {
        if (node.type === 'section') {
          return (
            <Section key={node.key} section={node} isFirst={index === 0} renderItem={renderItem} />
          );
        }

        return renderItem(node);
      })}
    </MenuContainer>
  );
}

// TODO: migrate consumers from `MenuItem` / `MenuSection` to `Menu.Item` / `Menu.Section`, which
// show that the parts belong to the menu, then remove the named exports. Both refer to the same
// collection components, so they can be mixed during the migration.
Menu.Item = MenuItem;
Menu.Section = MenuSection;

function Section(props) {
  const menuSection = useMenuSection({ 'aria-label': props.section['aria-label'] });
  const separator = useSeparator({ elementType: 'li' });

  return (
    <>
      {props.isFirst === false && <SeparatorContainer {...separator.separatorProps} />}

      <SectionContainer {...menuSection.itemProps}>
        <MenuContainer {...menuSection.groupProps}>
          {[...props.section.childNodes].map(props.renderItem)}
        </MenuContainer>
      </SectionContainer>
    </>
  );
}

function Item(props) {
  const itemRef = useRef();

  // No `onAction` here: useMenuItem already calls the item's own `onAction` and the one `useMenu`
  // registered for the menu, so passing it again would run the action twice.
  const menuItem = useMenuItem(
    {
      ...props.submenuTriggerProps,
      key: props.item.key,
      isDisabled: props.item.props.isDisabled,
    },
    props.treeState,
    itemRef,
  );

  const focusRing = useFocusRing();
  const isOpen = props.submenuTriggerProps?.isOpen === true;

  return (
    <MenuItemContainer
      {...mergeProps(menuItem.menuItemProps, focusRing.focusProps)}
      ref={mergeRefs([itemRef, props.ref])}
      aria-current={props.item.props['aria-current']}
      data-is-open={isOpen}
      data-is-pressed={menuItem.isPressed}
      data-is-focus-visible={focusRing.isFocusVisible}
      isDisabled={props.item.props.isDisabled}
      warning={props.item.props.warning}
    >
      {props.item.props.renderContent?.({ isPressed: menuItem.isPressed, isOpen }) ??
        props.item.rendered}
    </MenuItemContainer>
  );
}

// Opens its submenu beside it on hover, press, or the arrow key pointing into it.
function SubmenuItem(props) {
  const triggerRef = useRef();
  const submenuRef = useRef();
  const submenuState = useSubmenuTriggerState(
    { triggerKey: props.item.key },
    props.menuTriggerState,
  );
  const submenuTrigger = useSubmenuTrigger(
    { parentMenuRef: props.parentMenuRef, submenuRef, type: 'menu' },
    submenuState,
    triggerRef,
  );

  return (
    <>
      <Item
        item={props.item}
        treeState={props.treeState}
        submenuTriggerProps={submenuTrigger.submenuTriggerProps}
        ref={triggerRef}
      />

      {/* The submenu manages focus itself: it only takes focus when opened with the keyboard,
          and moves it back to this item when closed. Clicks on the parent menu reach their item. */}
      {submenuState.isOpen && (
        <Overlay
          placement={'end top'}
          {...props.item.props.submenuOverlayProps}
          targetRef={triggerRef}
          overlayTriggerState={submenuState}
          autoFocus={false}
          restoreFocus={false}
          containFocus={false}
          shouldBlockInteractOutside={false}
        >
          {props.item.props.renderSubmenu(submenuTrigger.submenuProps, submenuRef)}
        </Overlay>
      )}
    </>
  );
}

const MenuContainer = styled.ul`
  margin: 0;
  padding: 0;
  border: 0;
  outline: 0;
  list-style: none;
`;

const SectionContainer = styled.li`
  list-style: none;
`;

const SeparatorContainer = styled.li`
  height: 1px;
  margin: 5px var(--su-1);
  background-color: ${theme.color.line};
  list-style: none;
`;

const MenuItemContainer = styled.li`
  ${getBackgroundColor};
  padding: 0;
  outline: 0;
  border-radius: ${theme.borderRadius.default};
  transition: ${motion.transition.micro};
`;

function getBackgroundColor({ isDisabled, warning }) {
  const highlightColor =
    !isDisabled && warning ? theme.color.danger_background : theme.color.background_hover;

  return css`
    opacity: ${isDisabled ? 0.2 : 1};
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};

    &:hover,
    &[data-is-pressed='true'],
    &[data-is-open='true'] {
      background-color: ${highlightColor};
    }

    &[data-is-focus-visible='true'] {
      outline: 2px solid ${theme.color.focus_ring};
    }
  `;
}
